import React, { useMemo, useState } from "react";
import { manualStockOut } from "../../services/apiService";
import "./ManualStockOut.css";

const isPieceBarcode = (b = "") => /^PC-|^OPC-/i.test(String(b).trim());
const isBoxBarcode = (b = "") => /^BOX-|^OBX-/i.test(String(b).trim());

export default function ManualStockOut({ onSuccess }) {
  const [barcode, setBarcode] = useState("");
  const [qty, setQty] = useState(1);
  const [fullBoxOut, setFullBoxOut] = useState(true);
  const [loading, setLoading] = useState(false);

  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const isPiece = useMemo(() => isPieceBarcode(barcode), [barcode]);
  const isBox = useMemo(() => isBoxBarcode(barcode), [barcode]);

  const handleBarcodeChange = (value) => {
    setBarcode(value);
    setMsg("");
    setErr("");

    if (isPieceBarcode(value)) {
      setQty(1);
      setFullBoxOut(false);
      return;
    }

    if (isBoxBarcode(value)) {
      setFullBoxOut(true);
      return;
    }

    setFullBoxOut(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");

    const b = String(barcode || "").trim();
    const q = Number(qty);

    if (!b) {
      return setErr("Barcode is required.");
    }

    if (isPieceBarcode(b)) {
      if (q !== 1) {
        return setErr("Piece barcode quantity must be 1.");
      }
    }

    if (!fullBoxOut) {
      if (!q || q <= 0) {
        return setErr("Quantity must be greater than 0.");
      }

      if (!Number.isInteger(q)) {
        return setErr("Quantity must be a whole number.");
      }
    }

    try {
      setLoading(true);

      const res = await manualStockOut(b, fullBoxOut ? null : q, {
        fullBoxOut,
      });

      setMsg(res?.message || "✅ Manual stock out saved successfully.");
      setBarcode("");
      setQty(1);
      setFullBoxOut(true);

      if (onSuccess) onSuccess(res);
    } catch (e2) {
      setErr(e2?.message || "Failed to save manual stock out.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manual-out-page">
      <div className="manual-out-shell">
        <div className="manual-out-header">
          <h2>Manual Stock Out</h2>
          <p>Deduct inventory manually by scanning or entering a stock barcode.</p>
        </div>

        <form onSubmit={submit} className="manual-out-card">
          <div className="manual-out-card-header">
            <h3>Stock Deduction Form</h3>
            <span>Inventory Adjustment</span>
          </div>

          <div className="manual-out-form">
            <div className="manual-out-field">
              <label>Barcode</label>
              <input
                value={barcode}
                onChange={(e) => handleBarcodeChange(e.target.value)}
                placeholder="Scan or type barcode"
                autoFocus
              />

              <div className="manual-out-hint">
                Scan <strong>BOX- / OBX-</strong> to deduct the parent box and
                its child pieces. Scan <strong>PC- / OPC-</strong> for exact
                single-piece deduction.
              </div>
            </div>

            {isBox && (
              <div className="manual-out-check-row">
                <label className="manual-out-checkbox">
                  <input
                    type="checkbox"
                    checked={fullBoxOut}
                    onChange={(e) => setFullBoxOut(e.target.checked)}
                  />
                  <span>Out entire box including all child pieces</span>
                </label>

                {fullBoxOut && (
                  <div className="manual-out-hint warning">
                    Enabled: scanning OBX/BOX will OUT all available children
                    under that parent barcode.
                  </div>
                )}
              </div>
            )}

            <div className="manual-out-field">
              <label>Quantity</label>
              <input
                type="number"
                min={1}
                value={fullBoxOut ? "" : qty}
                disabled={isPiece || fullBoxOut}
                onChange={(e) => setQty(e.target.value)}
                placeholder={fullBoxOut ? "Auto remaining quantity" : "Enter quantity"}
              />

              {isPiece && (
                <div className="manual-out-hint warning">
                  Piece barcode detected. Quantity is fixed to 1.
                </div>
              )}

              {isBox && fullBoxOut && (
                <div className="manual-out-hint warning">
                  Full box OUT is enabled. Quantity will be based on the current
                  remaining box quantity.
                </div>
              )}
            </div>

            <div className="manual-out-status">
              <span
                className={`status-pill ${
                  isPiece ? "piece" : isBox ? "box" : "general"
                }`}
              >
                {barcode.trim()
                  ? isPiece
                    ? "Piece Barcode"
                    : isBox && fullBoxOut
                    ? "Box Barcode - Full Box OUT"
                    : isBox
                    ? "Box Barcode - Manual Quantity"
                    : "General Barcode"
                  : "Waiting for barcode input"}
              </span>
            </div>

            <button type="submit" className="manual-out-btn" disabled={loading}>
              {loading ? "Saving..." : "Manual Stock Out"}
            </button>

            {msg && <div className="manual-out-success">{msg}</div>}
            {err && <div className="manual-out-error">{err}</div>}
          </div>
        </form>
      </div>
    </div>
  );
}